import { createStyles, makeStyles } from "@material-ui/core";
import {
    AttachmentLinkCollection,
    DisplayField,
    Text,
    Layout,
    LayoutContainer,
    Link,
    RelodeTheme,
} from "@relode/core";
import { PhoneLink } from "@relode/core/components/phoneLink";
import { EmailLink } from "@relode/core/components/emailLink";
import * as React from "react";
import { Tag } from "@relode/core/domain";

import { CandidateHighlights, CandidateAdditionalInfo } from "../../types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            linkRow: {
                display: "flex",
                flexDirection: "row",
                margin: `${theme.spacing(1)}px 0px`,
                justifyContent: "space-between",
                alignItems: "center",
            },
            link: {
                overflowWrap: "anywhere",
            },
            label: {
                color: theme.relode.colors.label.main,
                marginTop: 4,
            },
        }),
    { name: "CandidateHighlights" }
);

interface CandidateProfileProps {
    highlights: CandidateHighlights;
    additionalInfo: CandidateAdditionalInfo;
}

export const CandidateProfile = (props: CandidateProfileProps) => {
    const classes = useStyles({});
    const { highlights, additionalInfo } = props;

    if (!highlights) return null;

    const renderIsWillingToRelocateResponse = (tagId: number) => {
        switch (tagId) {
            case Tag.WillingToRelocateYes.tagId: {
                return "Yes";
            }
            case Tag.WillingToRelocateNo.tagId: {
                return "No";
            }
            default: {
                return "not yet answered";
            }
        }
    };

    return (
        <LayoutContainer>
                <Layout mode="100">
                    <Text variant="h2">profile</Text>

                    {!highlights.desiredSalaryIsRate &&
                    highlights.desiredSalaryDescription?.length > 0 ? (
                        <DisplayField
                            label="desired salary"
                            value={highlights.desiredSalaryDescription}
                            preserveTextFormat
                        />
                    ) : null}

                    <DisplayField
                        label="current location"
                        value={highlights.primaryLocationName}
                    />

                    {!additionalInfo.isUserPrimaryLocationWithinJobLocation ? (
                        <>
                            <DisplayField
                                label="willing to relocate?"
                                value={renderIsWillingToRelocateResponse(
                                    additionalInfo.isWillingToRelocate?.tagId
                                )}
                                data-cy="willingToRelocate"
                            />
                        </>
                    ) : null}

                    <DisplayField
                        label="contact info"
                        hidden={!highlights.canContactLead}
                        component={
                            <div>
                                <PhoneLink
                                    highlightType="internal"
                                    phone={highlights.phoneNumber}
                                    variant="textAndIcon"
                                    data-cy="phoneLink"
                                />
                                <EmailLink
                                    email={highlights.email}
                                    variant="textAndIcon"
                                    highlightType="internal"
                                    data-cy="emailLink"
                                />
                            </div>
                        }
                    />

                    {highlights.attachments?.length > 0 ? (
                        <>
                            <DisplayField
                                label="attachments"
                                component={
                                    <AttachmentLinkCollection
                                        id="highlights.attachments"
                                        attachments={highlights.attachments}
                                    />
                                }
                                data-cy="attachmentsField"
                            />
                        </>
                    ) : null}

                    {highlights.links?.length > 0 ? (
                        <>
                            <DisplayField
                                label="links"
                                component={
                                    <div data-cy="linksDiv">
                                        {highlights.links?.map(
                                            (link: string, i: number) => (
                                                <div
                                                    className={classes.linkRow}
                                                    key={i}
                                                >
                                                    <Link
                                                        className={classes.link}
                                                        href={link}
                                                        target="_blank"
                                                        key={i}
                                                        highlightType="internal"
                                                        usePrimaryColorLink={
                                                            true
                                                        }
                                                    >
                                                        {link}
                                                    </Link>
                                                </div>
                                            )
                                        )}
                                    </div>
                                }
                            />
                        </>
                    ) : null}
                </Layout>
        </LayoutContainer>
    );
};
